import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";

/**
 * PageVisibility — loads the unpublished-pages list from the backend
 * once on app boot and enforces redirects when a visitor lands on a
 * page that has been unpublished from the CMS.
 *
 * Strategy:
 *  - GET /api/page-visibility/list returns ONLY unpublished entries
 *    (default = published, so this stays a small payload).
 *  - On miss, we behave as "published" — failing open avoids breaking
 *    the site if the API hiccups.
 *  - Admin routes (/admin/*) and the actively-visited URL itself are
 *    never blocked — admins must still be able to log in and re-publish.
 */

const API_URL = (typeof window !== 'undefined' && window.location?.origin) || process.env.REACT_APP_BACKEND_URL;
const VisibilityCtx = createContext({ unpublished: {}, refresh: () => {} });

export function PageVisibilityProvider({ children }) {
  const [unpublished, setUnpublished] = useState({});

  const refresh = useMemo(() => async () => {
    try {
      const r = await axios.get(`${API_URL}/api/page-visibility/list`, { timeout: 6000 });
      const map = {};
      for (const row of r.data?.unpublished || []) {
        map[row.path] = row.redirect_to || "/";
      }
      setUnpublished(map);
    } catch {
      // fail-open
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <VisibilityCtx.Provider value={{ unpublished, refresh }}>
      {children}
    </VisibilityCtx.Provider>
  );
}

export const useVisibility = () => useContext(VisibilityCtx);

/**
 * Wrap the public-facing Routes tree with <PageVisibilityGuard>. It uses
 * the current location to redirect when the path is on the unpublished
 * list. Admin / login paths are exempt.
 */
export function PageVisibilityGuard({ children }) {
  const { unpublished } = useVisibility();
  const { pathname } = useLocation();

  const exempt =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/totp");

  if (!exempt && unpublished[pathname]) {
    const target = unpublished[pathname];
    // Avoid infinite redirect loops if the target itself is unpublished.
    if (target !== pathname) {
      return <Navigate to={target} replace />;
    }
  }
  return children;
}
